/** Estado e ações do formulário de tarefa (sheet lateral). */
import { useState, useEffect, useMemo, useCallback, type ChangeEvent, type FormEvent } from 'react';
import type { Task } from '../../../types';
import { parseDateTimeParts } from '../../../components/ui/DateTimeField';
import { getLeadsSelect } from '../../../services/modules/taskService';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { notify } from '../../../utils/toast';
import {
  buildTaskFormPayload,
  TASK_DESCRIPTION_MAX,
  TASK_TITLE_MAX,
  type TaskFormFields,
  type LeadOption,
} from '../lib/taskFormPayload';

export type { LeadOption };

const EMPTY_FORM: TaskFormFields = {
  title: '',
  description: '',
  dueDate: '',
  dueTime: '',
  isCompleted: false,
  leadId: '',
};

function taskToForm(task?: Task | null): TaskFormFields {
  if (!task) return { ...EMPTY_FORM };
  const { date: dueDate, time: dueTime } = parseDateTimeParts(task.dueDate);
  return {
    title: task.title || '',
    description: task.description || '',
    dueDate,
    dueTime,
    isCompleted: task.isCompleted || false,
    leadId: task.leadId || '',
  };
}

interface UseTaskFormSheetParams {
  isOpen: boolean;
  task?: Task | null;
  onSave: (taskData: Partial<Task>) => Promise<void>;
}

export function useTaskFormSheet({ isOpen, task, onSave }: UseTaskFormSheetParams) {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const isEditing = Boolean(task);

  const [formData, setFormData] = useState<TaskFormFields>(EMPTY_FORM);
  const [leadOptions, setLeadOptions] = useState<LeadOption[]>([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [leadSearchTerm, setLeadSearchTerm] = useState('');
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [selectedLeadName, setSelectedLeadName] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setFormData(taskToForm(task));
    setLeadSearchTerm('');
    setSelectedLeadName(task?.lead?.name || '');
    setShowLeadDropdown(false);
    setIsLoadingLeads(true);
    getLeadsSelect(currentOrganization?.id)
      .then(setLeadOptions)
      .catch(() => notify.error('Não foi possível carregar as negociações.'))
      .finally(() => setIsLoadingLeads(false));
  }, [isOpen, task, currentOrganization?.id]);

  const filteredLeads = useMemo(() => {
    const q = leadSearchTerm.trim().toLowerCase();
    if (!q) return leadOptions;
    return leadOptions.filter(o => o.label.toLowerCase().includes(q));
  }, [leadOptions, leadSearchTerm]);

  const showLeadList = showLeadDropdown && !selectedLeadName;
  const showLeadEmpty = showLeadList && Boolean(leadSearchTerm.trim()) && filteredLeads.length === 0 && !isLoadingLeads;

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, type } = e.target;
    const value = type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleDateTimeChange = useCallback(({ date, time }: { date: string; time: string }) => {
    setFormData(prev => ({ ...prev, dueDate: date, dueTime: time }));
  }, []);

  const selectLead = useCallback((option: LeadOption) => {
    setFormData(prev => ({ ...prev, leadId: option.value }));
    setSelectedLeadName(option.label);
    setLeadSearchTerm('');
    setShowLeadDropdown(false);
  }, []);

  const clearLead = useCallback(() => {
    setFormData(prev => ({ ...prev, leadId: '' }));
    setSelectedLeadName('');
    setLeadSearchTerm('');
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!user?.id) {
        notify.error('Sessão inválida. Faça login novamente para criar a tarefa.');
        return;
      }
      const creator: Task['assignedTo'] = {
        id: user.id,
        name:
          (user.user_metadata?.full_name as string | undefined) ??
          (user.email as string | undefined) ??
          'Usuário',
      };
      const result = buildTaskFormPayload(formData, {
        assignedTo: creator,
        leadOptions,
        existingLeadId: task?.leadId,
      });
      if (result.ok === false) {
        notify.warning(result.message);
        return;
      }

      setIsSaving(true);
      try {
        await onSave(result.payload);
        if (!isEditing) {
          setFormData({ ...EMPTY_FORM });
          setSelectedLeadName('');
        }
      } finally {
        setIsSaving(false);
      }
    },
    [formData, user, leadOptions, task?.leadId, onSave, isEditing],
  );

  return {
    isEditing,
    formData,
    isSaving,
    isLoadingLeads,
    leadSearchTerm,
    setLeadSearchTerm,
    setShowLeadDropdown,
    selectedLeadName,
    filteredLeads,
    showLeadList,
    showLeadEmpty,
    handleChange,
    handleDateTimeChange,
    handleSubmit,
    selectLead,
    clearLead,
    titleMaxLength: TASK_TITLE_MAX,
    descriptionMaxLength: TASK_DESCRIPTION_MAX,
  };
}
